import React, { useCallback, useMemo } from 'react';
import onClickOutside from 'react-onclickoutside';
import { connect } from 'react-redux';
import { useAsync } from 'react-use';
import cn from 'classnames';

import trackEvent from 'actions/analytics/trackEvent.action';
import getProcedureLog from 'actions/scheduler/getProcedureLog.action';
import REVERT_ICO from 'assets/images/btn-revert-grey.svg';
import HISTORY_ICO from 'assets/images/History-icon-purple.svg';
import CLOSE_ICO from 'assets/images/ic_close_black_48px.svg';
import TRACK_EVENT_TYPE from 'constants/salon/trackEventType.const';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import mapActionsToDispatchToProps from 'utils/core/mapActionsToDispatchToProps.util';
import call from 'utils/functions/call.util';
import logger from 'utils/logger.util';
import isFullArray from 'utils/predicates/array/isFullArray.util';

const L = logger('RevertButton'); // eslint-disable-line no-unused-vars

const RevertDropdown = ({ onClose, onRevertProcedure, procedureId, getProcedureLog }) => {
  const salonId = useCurrentSalonId();

  const {
    value: procedureLog,
    loading,
    error,
  } = useAsync(async () => {
    const result = await getProcedureLog(salonId, procedureId);
    L.debug('RevertDropdown()', 'useAsync()', 'result:', result);
    return result.json;
  }, [getProcedureLog, procedureId, salonId]);

  const selections = useMemo(
    () =>
      isFullArray(procedureLog)
        ? procedureLog.map((item) => ({
            icon: REVERT_ICO,
            pk: item?.pk,
            date: item?.data?.date?.value,
            time: item?.data?.time?.value,
            stylist: item?.data?.stylist?.value,
            isDateChanged: item?.data?.date?.is_changed,
            isTimeChanged: item?.data?.time?.is_changed,
            isStylistChanged: item?.data?.stylist?.is_changed,
            onSelectionRevert: () => call('RevertDropdown.onRevertProcedure', onRevertProcedure, { pk: item?.pk }),
          }))
        : [],
    [onRevertProcedure, procedureLog],
  );

  const displayTitle = loading ? 'Loading History...' : isFullArray(selections) ? 'Change History' : 'No history';

  if (error) L.warn('RevertDropdown()', error);

  return (
    <div data-bem="RevertDropdown" className="dropdown-component animated zoomIn">
      <div className="dropdown-menu">
        <span className="close-history" role="button" tabIndex={0} onClick={onClose}>
          <img src={CLOSE_ICO} alt="" />
        </span>

        <div className="dropdown-title">{displayTitle}</div>

        <div className="scroll-container">
          {selections.map((s) => (
            <button className="revert-log-list" key={s?.pk} tabIndex="0" onClick={s?.onSelectionRevert}>
              {s?.icon && <img src={s?.icon} alt={s?.stylist} className="icon" />}

              <div
                className={cn({
                  'changed-item': s?.isDateChanged,
                  'unchanged-item': !s?.isDateChanged,
                })}
              >
                {s?.date}
              </div>

              <div
                className={cn({
                  'changed-item': s?.isTimeChanged,
                  'unchanged-item': !s?.isTimeChanged,
                })}
              >
                {s?.time}
              </div>

              <div
                className={cn({
                  'changed-item': s?.isStylistChanged,
                  'unchanged-item': !s?.isStylistChanged,
                })}
              >
                {s?.stylist}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const RevertDropdownHoc = connect(
  (state) => ({
    procedureLogItems: state?.scheduler?.procedureLog?.response,
  }),
  mapActionsToDispatchToProps({
    getProcedureLog,
  }),
)(RevertDropdown);

const RevertDropdownModal = onClickOutside(
  class RevertDropdownInside extends React.Component {
    handleClickOutside = () => void call('RevertDropdownInside.onClose', this.props.onClose);

    render() {
      return <RevertDropdownHoc {...this.props} />;
    }
  },
);

const RevertButton = ({ onClose, onOpen, withRevert, onRevertProcedure, trackEvent, procedureId }) => {
  const handleClick = useCallback(
    (ev) => {
      ev.stopPropagation(); // FIXME: remove stopPropagation();
      call('RevertButton.onOpen', onOpen);
      call('RevertButton.trackEvent', trackEvent, { event: TRACK_EVENT_TYPE.viewChangeHistory });
    },
    [onOpen, trackEvent],
  );

  return (
    <div data-bem="RevertButton" className="card-modal-kebap-container">
      <button className="revert-button" onClick={handleClick}>
        <small>View change history</small>
        <img src={HISTORY_ICO} alt="" />
      </button>

      {fromAnyToBool(withRevert) && (
        <RevertDropdownModal procedureId={procedureId} onRevertProcedure={onRevertProcedure} onClose={onClose} />
      )}
    </div>
  );
};

const RevertButtonHoc = connect(null, mapActionsToDispatchToProps({ trackEvent }))(RevertButton);

export default RevertButtonHoc;
